import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, InputAdornment, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EmailIcon from "@iconify/icons-mdi/email";
import { InlineIcon } from "@iconify/react";
import { FormikProvider, useFormik } from "formik";
import { useMyHrChangeRequestsEnabled } from "my-hr/hooks";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcSettingSwitchFormik,
  OcTextFieldFormik,
} from "oc/components";
import { useOcProperty } from "oc/hooks";
import { useOcFormikHelper } from "oc/utils";
import * as Yup from "yup";

type MyHrSettingsChangeRequestsCardProps = {
  changeRequestsEnabled: boolean; // TODO f I can't seem to find what this does or how.
  isMobile: boolean;
};

export const MyHrSettingsChangeRequestsCard = ({
  changeRequestsEnabled,
  isMobile,
}: MyHrSettingsChangeRequestsCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { setState: setChangeRequestsEnabled } = useMyHrChangeRequestsEnabled();

  const {
    data: changeRequestsEnabledValue,
    loading: changeRequestsEnabledLoading,
  } = useOcProperty({
    resource: "system",
    key: "changeRequestsEnabled",
  });

  const {
    data: changeRequestsEmailValue,
    loading: changeRequestsEmailLoading,
  } = useOcProperty({
    resource: "system",
    key: "changeRequestsEmail",
  });

  const isPropsLoading = changeRequestsEnabledLoading || changeRequestsEmailLoading;

  const initialValues = useMemo(
    () => ({
      changeRequestsEnabled: changeRequestsEnabledValue === "true",
      changeRequestsEmail: changeRequestsEmailValue ?? "",
    }),
    [changeRequestsEnabledValue, changeRequestsEmailValue]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: () => {
      updateProperties();
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: Yup.object({
      changeRequestsEmail: Yup.string()
        .email(t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"))
        .matches(
          /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/,
          t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"),
        ),
    }),
  });

  const { loadingState, updateProperties } = useOcFormikHelper({
    formik,
    defaultResource: "system",
    valuesExtraData: {
      changeRequestsEmail: {
        key: "changeRequestsEmail",
        loading: changeRequestsEmailLoading,
        type: "input",
      },
      changeRequestsEnabled: {
        key: "changeRequestsEnabled",
        loading: changeRequestsEnabledLoading,
        type: "switch",
        returnType: "boolean",
      },
    },
    onCompleted: (data) => {
      if (data?.updateProperty.key === "changeRequestsEnabled") {
        setChangeRequestsEnabled(formik.values.changeRequestsEnabled);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleOpenDialog = () => {
    setShowConfirmDialog(true);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          formik.submitForm();
        }}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard
        label={t("MY_HR_CHANGE_REQUEST_SETTINGS")}
        loading={loadingState || isPropsLoading}
      >
        {!isPropsLoading && (
          <FormikProvider value={formik}>
            <OcSettingSwitchFormik
              name="changeRequestsEnabled"
              id="prohibitChangeRequests"
              label={t("MY_HR_CHANGE_REQUEST_SETTINGS")}
              info={t("MY_HR_CHANGE_REQUEST_SETTINGS_INFO")}
              isMobile={isMobile}
              disabled={!changeRequestsEnabled || loadingState}
            />
            <Box height="16px" />
            <Box
              width={isMobile ? "auto" : "448px"}
              paddingLeft={isMobile ? "0px" : "48px"}
            >
              <Box height="16px" />
              <Typography
                variant="body1"
                style={{ color: theme.palette.gray80.main }}
              >
                {t("MY_HR_CHANGE_REQUEST_EMAIL")}
              </Typography>
              <Box height="16px" />
              <OcTextFieldFormik
                name="changeRequestsEmail"
                variant="outlined"
                label="Email"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InlineIcon
                        height="24px"
                        icon={EmailIcon}
                        color={theme.palette.gray60.main}
                      />
                    </InputAdornment>
                  ),
                }}
                disabled={
                  !formik.values.changeRequestsEnabled || loadingState
                }
              />
            </Box>
            <OcCardButtonBar
              handleRightButton={handleCancel}
              rightButtonColor="secondary"
              rightButtonText={t("OC_CANCEL")}
              handlePrimaryButton={handleOpenDialog}
              primaryButtonText={t("OC_SAVE")}
              primaryButtonDisabled={loadingState || !formik.dirty}
              primaryButtonId="change-requests-button"
            />
          </FormikProvider>
        )}
      </OcCard>
    </>
  );
};

export default MyHrSettingsChangeRequestsCard;
